import { action, makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Button, ButtonProps, Tooltip } from 'reactstrap';
import { tryGetAppContext } from '~/lib/AppContext';
import { idGenerator } from '~/lib/helper/IdHelper';
import { announce, DialogAnnouncerContext } from './DialogAnnouncer';

interface CopyToClipboardButtonProps extends ButtonProps {
    text: string;
    tooltipText?: string;
}

const idGen = idGenerator('copy-to-clipboard-button');

@observer
export default class CopyToClipboardButton extends React.Component<CopyToClipboardButtonProps> {
    static contextType = DialogAnnouncerContext;
    declare context: React.ContextType<typeof DialogAnnouncerContext>;

    @observable
    showCopiedTooltip = false;

    id = idGen();

    constructor(props: CopyToClipboardButtonProps) {
        super(props);
        makeObservable(this);
    }

    // Shared by the visible tooltip and the screen-reader announcement so the two can't drift.
    private get copiedMessage(): string {
        return this.props.tooltipText ?? 'Copied to clipboard';
    }

    private async doCopy() {
        await navigator.clipboard.writeText(this.props.text);

        runInAction(() => {
            this.showCopiedTooltip = true;
        });

        // The tooltip is a role="tooltip" popover, which is not a live region — announce the
        // copy politely so screen-reader users get the same confirmation sighted users see.
        announce(this.context, this.copiedMessage);
    }

    @action
    closeTooltip() {
        this.showCopiedTooltip = false;
    }

    render() {
        const { tooltipText, text, ...buttonProps } = this.props;
        return (
            <>
                <Button
                    id={this.id}
                    onClick={(e) => {
                        e.preventDefault();
                        this.doCopy().catch((err) => {
                            // The modal shows only the summary, so keep the raw rejection in the console for diagnostics.
                            console.error(err);
                            tryGetAppContext()?.error(err, { errorSummary: 'Unable to copy to clipboard' });
                        });
                    }}
                    {...buttonProps}
                    aria-label={buttonProps['aria-label'] ?? buttonProps.title ?? 'Copy to clipboard'}
                >
                    <i className="icon ion-md-copy" aria-hidden></i>
                </Button>
                <Tooltip
                    placement="top"
                    isOpen={this.showCopiedTooltip}
                    autohide={true}
                    target={this.id}
                    toggle={() => this.closeTooltip()}
                    trigger="focus"
                >
                    {this.copiedMessage}
                </Tooltip>
            </>
        );
    }
}
